<i18n
  src="@/locales/views/agro_exporter/finances/container_outcomes/show_container.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_item.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_process_containers')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id
    }"
  >
  </go-back-link>

  <bread-crumbs :labels="[module_name, agro_exporter_process.name]" />

  <template v-if="load_data_general">
    <div class="mt-8">
      <container-info
        :agro_exporter_id="agro_exporter_id"
        :container="container"
      >
        <template #assign-provider>
          <edit-button
            @click.prevent="goToAssignProvider()"
            :label="$t('labels.assign_provider')"
          />
        </template>
      </container-info>
    </div>

    <div class="mt-8 flex flex-row">
      <h1 class="page-title">
        <i class="fa-solid fa-file-invoice-dollar"></i>
        <span class="space-icon"></span>
        {{ $t("labels.title_org_invoices") }}
      </h1>
    </div>
    <div class="mt-8 flex flex-row justify-between">
      <div class="panel-plain-label space-x-4">
        <badge-default
          :label="
            org_invoices.length + ' ' + $t('labels.total_number_org_invoices')
          "
        />
        <badge-default
          :label="
            org_invoices_paid +
            ' ' +
            $t('labels.total_number_org_invoices_paid')
          "
        />
      </div>
      <new-button
        @click.prevent="goToNewExportInvoice()"
        :label="$t('labels.new_button')"
      />
    </div>

    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.invoice_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.invoice_code") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.invoice_issuer") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.receipt_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.amount") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.detraction_total") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.amount_paid") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.to_paid") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.scheduled_payment_at") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.paid") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.fairtrade") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice.fairtrade_incentive") }}
                  </th>
                  <th scope="col" class="table-header-cell"></th>
                  <th scope="col" class="table-header-cell"></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="org_invoice in org_invoices"
                  :key="org_invoice.id"
                >
                  <td class="table-cell">
                    {{ dateFormatter(org_invoice.invoice_date) }}
                  </td>
                  <td
                    class="table-cell-main link"
                    @click="goToShowExportInvoice(org_invoice)"
                  >
                    {{ org_invoice.invoice_code }}
                  </td>
                  <td
                    class="table-cell"
                  >
                    {{ org_invoice.invoice_issuer?.fullname || org_invoice.invoice_issuer?.name }}
                  </td>
                  <td class="table-cell">
                    {{ dateFormatter(org_invoice.receipt_date) }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice.amount,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice.detraction_total,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice.amount_paid,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice.to_paid,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell">
                    {{ dateFormatter(org_invoice.scheduled_payment_at) }}
                  </td>
                  <td class="table-cell">
                    <yes-no-label :value="org_invoice.paid" />
                  </td>
                  <td class="table-cell">
                    <yes-no-label :value="org_invoice.fairtrade" />
                  </td>
                  <td class="table-cell">
                    <yes-no-label :value="org_invoice.fairtrade_incentive" />
                  </td>
                  <td class="table-cell">
                    <org-invoice-details
                      :org_invoice="org_invoice"
                      icon="fa-solid fa-eye"
                    />
                  </td>
                  <td class="table-cell-action">
                    <pay-org-invoice-button
                      :paid="org_invoice.paid"
                      @payExportInvoice="updateExportInvoice(org_invoice)"
                    ></pay-org-invoice-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/finances/container_outcomes/show_container.js";
import ManageExportInvoiceRequester from "@/views/agro_exporter/requesters/finances/container_outcomes/manage_org_invoice.js";
import ContainerInfo from "@/components/views/agro_exporter/finances/container_outcomes/ContainerInfo.vue";
import PayOrgInvoiceButton from "@/components/views/agro_exporter/finances/container_outcomes/PayOrgInvoiceButton.vue";
import OrgInvoiceDetails from "@/components/views/agro_exporter/finances/container_outcomes/OrgInvoiceDetails.vue";
export default {
  name: "AeFinancesContainerOutcomesShowContainer",
  components: { ContainerInfo, PayOrgInvoiceButton, OrgInvoiceDetails },
  props: {
    agro_exporter_process_id: { required: true },
    agro_exporter_id: { required: true },
    container_id: { required: true }
  },
  data() {
    return {
      load_data_general: false,
      proc_save_button: false,
      proc_button_destroy: false,
      agro_exporter_process: {},
      container: {},
      org_invoices: []
    };
  },
  computed: {
    org_invoices_paid: function () {
      return this.org_invoices.filter(org_invoice => org_invoice.paid).length;
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchContainer();
      await this.fetchOrgInvoices();
      this.load_data_general = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer(
        this.agro_exporter_id,
        this.container_id
      );
      this.container = response.data;
    },
    fetchOrgInvoices: async function () {
      let response = await Requester.fetchOrgInvoices(
        this.agro_exporter_id,
        this.container_id
      );
      this.org_invoices = response.data;
      this.org_invoices.forEach(org_invoice => {
        org_invoice["to_paid"] = this.toPaid(org_invoice);
      });
    },
    toPaid: function (org_invoice) {
      let total = org_invoice.amount - org_invoice.amount_paid;
      org_invoice.org_invoice_sum_discounts.forEach(discount => {
        const discount_split = discount.split(" ");
        if (discount_split[0] == org_invoice.currency.symbol) {
          total -= Number(discount_split[1]);
        }
      });
      return total;
    },
    updateExportInvoice: async function (org_invoice) {
      this.proc_save_button = true;
      try {
        let response = await ManageExportInvoiceRequester.update(
          this.agro_exporter_id,
          org_invoice.id,
          {
            paid: !org_invoice.paid
          }
        );
        let org_invoice_update = response.data.org_invoice;
        org_invoice.paid = org_invoice_update.paid;
        org_invoice.amount_paid = org_invoice_update.amount_paid;
        this.container.org_invoices_paid =
          this.org_invoices.filter(invoice => invoice.paid).length ==
          this.org_invoices.length;
        this.registerSuccessNotification({
          title: response.data.success_message
        });
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_save_button = false;
    },
    goToAssignProvider: function () {
      this.$router.push({
        name: this.routeName("assign_provider"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          container_id: this.container_id
        }
      });
    },
    goToNewExportInvoice: function () {
      this.$router.push({
        name: this.routeName("new_org_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          container_id: this.container_id
        }
      });
    },
    goToShowExportInvoice: function (org_invoice) {
      this.$router.push({
        name: this.routeName("show_org_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          container_id: this.container_id,
          org_invoice_id: org_invoice.id
        }
      });
    },
    goToShowProcess: function () {
      this.$router.push({
        name: this.routeName("show_process_org_invoices"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id
        }
      });
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.loadData())
    );
  }
};
</script>
